const { Long, BN } = require('@zilliqa-js/util');
const { Zilliqa, toBech32Address, units, bytes, fromBech32Address } = require('@zilliqa-js/zilliqa');
const Buffer = require('buffer/').Buffer;
const CryptoJS = require('crypto-js');

(() => {
    // Base URL for opened authentication window
    let baseUri = 'https://client.zil-wallet.com';

    let zeevesSinkUrl = 'https://zeeves.io/api/blockchain/external-transaction';

    // maximum attempts to resend RPC requests to blockchain
    const maxAttempts = 3;

    const events = {
        ENCRYPTION_KEY: 'encryptionKey',
        AUTH: 'auth',
        OPENED: 'opened'
    };

    const sinkTransactionToZeeves = async (txnHash) => {
        const zilliqaAddress = localZeeves.wallet.accounts[Object.keys(localZeeves.wallet.accounts)[0]].bech32Address;
        const siteUrl = window.location.origin;
        fetch(zeevesSinkUrl, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                txnHash,
                zilliqaAddress,
                siteUrl
            })
        });
    }

    const openCenteredWindow = (url, title, w, h) => {
        const dualScreenLeft = window.screenLeft ? window.screenLeft : window.screenX;
        const dualScreenTop = window.screenTop ? window.screenTop  : window.screenY;
    
        const width = window.innerWidth ? window.innerWidth : document.documentElement.clientWidth ? document.documentElement.clientWidth : screen.width;
        const height = window.innerHeight ? window.innerHeight : document.documentElement.clientHeight ? document.documentElement.clientHeight : screen.height;
    
        const systemZoom = width / window.screen.availWidth;
        const left = (width - w) / 2 / systemZoom + dualScreenLeft
        const top = (height - h) / 2 / systemZoom + dualScreenTop
        const newWindow = window.open(url, title, `scrollbars=yes,width=${w/systemZoom}, height=${h / systemZoom},top=${top},left=${left}`)

        if (window.focus && newWindow) {
            newWindow.focus();
        }

        return newWindow;
    }

    const createZilliqa = () => new Zilliqa('https://api.zilliqa.com');

    // original zilliqa, which is used as a target for blockchain calls
    const originalZilliqa = createZilliqa();

    // prototype for Zeeves, which proxies calls to "originalZilliqa"
    const zilliqa = createZilliqa();

    // function to generate random UUID
    const randomUuid = () => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const randomChar = Math.random() * 16 | 0;
        const char = c === 'x' ? randomChar : (randomChar & 0x3 | 0x8);
        return char.toString(16);
    });

    // listenening for messages with callback
    // when message received or timeout reached - listener is deleted
    const listenMessages = (callback) => {
        const eventMethod = window.addEventListener ? "addEventListener" : "attachEvent";
        const addListener = window[eventMethod];
        const messageEvent = eventMethod === "attachEvent" ? "onmessage" : "message";
        const detach = (e, handler) => {
            if (window.removeEventListener) {
                window.removeEventListener(e, handler, false);
            } else if (window.detachEvent) {
                window.detachEvent(e, handler);
            } else {
                delete window[e];
            }
        }
        const handler = (e) => {
            try {
                callback(e);
            } finally {
                detach(e, handler);
            }
        };
        addListener(messageEvent, handler, false);
        setTimeout(() => {
            detach(messageEvent, handler);
        }, 60*1000);
    }

    // waiting for popup to be opened
    const waitOpened = async () => {
        return new Promise((resolve, reject) => {
            listenMessages((e) => {
                if (e.origin === baseUri) {
                    const key = e.message ? "message" : "data";
                    const data = e[key];
                    if (typeof(data) === 'string') {
                        const messagePayload = atob(data);
                        if (messagePayload === events.OPENED) {
                            resolve();
                        }
                    }
                } else {
                    window.console.error('Unknown origin of event - ' + e.origin);
                }
            })
        });
    }

    // timeout in MS for calling SDK methods
    const CALL_TIMEOUT = 120 * 1000;

    // getting wallet information to return to ZilSwap webapp
    // method also sets Zeeves wallet defaultAccount properties
    // which are used for fetching balances and transactions
    const getWalletInfo = () => {
        if (Object.keys(zilliqa.wallet.accounts).length === 0) {
            return null;
        }

        const byteAddress = Object.keys(zilliqa.wallet.accounts)[0];
        const bech32Address = toBech32Address(byteAddress, false);

        window.Zeeves.wallet.defaultAccount = {
            base16: byteAddress,
            bech32: bech32Address,
            address: byteAddress,
        };

        return {
            byte20: byteAddress,
            bech32: bech32Address,
        };
    }

    // Zeeves
    const localZeeves = Object.create(zilliqa);

    localZeeves.properties = {
        isDebug: true
    };

    const warn = (msg) => {
        if (localZeeves.properties.isDebug) {
            window.console.warn(msg);
        }
    }

    // for ZilPay zilswap-webapp calls contracts.at(...).call()
    // which internall calls prepareTx method
    // by default, inside zilliqa.js this causes waiting for transaction
    // confirmation, which is not needed here as far as webapp
    // itself observes txns by their ids
    // 
    // so origin prepareTx method is overwritten without waiting for
    // confirmation
    //
    // it also sets public key for transaction before sign() if not set
    localZeeves.contracts.at = (e,t,r,i,n) => {
        const contractFromZilliqa = originalZilliqa.contracts.at(e,t,r,i,n);

        // re-define callwithoutconfirm to call, cause it's the same
        // after prepareTx definition below
        // p.s. this is zilpay-like style
        contractFromZilliqa.callWithoutConfirm = contractFromZilliqa.call;

        contractFromZilliqa.prepareTx = async (tx) => {
            warn('Checking nonce...');
            if (tx.nonce) {
                const intNonce = parseInt(tx.nonce);
                const balanceResponse = await localZeeves.wallet.provider.send(
                    'GetBalance', 
                    Zeeves.wallet.accounts[Zeeves.wallet.defaultAccount.address].address.substr(2)
                );
                if (balanceResponse && balanceResponse.result) {
                    const currentNonce = balanceResponse.result.nonce;
                    if (parseInt(currentNonce) > intNonce) {
                        warn('Replacing nonce ' + intNonce + ' in transaction with current nonce from blockchain ' 
                            + currentNonce + ' cause it is higher');
                        tx.nonce = currentNonce;
                        tx.txParams.nonce = currentNonce;
                    } else {
                        warn('Nonce is okay');
                    }
                }
            }
            warn("Signing txn...")
            if (tx.nonce && (!tx.pubKey || !tx.txParams.pubKey)) {
                const pubKey = Zeeves.wallet.accounts[Zeeves.wallet.defaultAccount.address].publicKey;
                warn('Public key not set in transaction, setting explicitely to ' + pubKey);
                tx.pubKey = pubKey;
                tx.txParams.pubKey = pubKey;
            }
            const signedTx = await Zeeves.wallet.sign(tx);
            warn("Successfully signed. Signature = " + signedTx.signature);
            return await localZeeves.wallet.provider.send('CreateTransaction', {
                ...signedTx.txParams, priority: signedTx.toDS
            });
        }
        return contractFromZilliqa;
    }

    // Interceptor for Blockchain calls from ZilSwap webapp
    // for 'CreateTransaction' method returning result is customized
    // in ZilPay style. For other methods - just returning
    // results from blockchain
    localZeeves.wallet.provider.send = async (method, ...params) => {
        try {
            let attemptsCount = 0;
            while (attemptsCount <= maxAttempts) {
                try {
                    if (method === 'CreateTransaction') {
                        warn('CreateTransaction call. Params - ' + JSON.stringify(params));
                        warn('Calling blockchain... ');
                        const callResult = await originalZilliqa.wallet.provider.send(method, ...params);
                        warn('Got CreateTransaction call result - ' + JSON.stringify(callResult));
                        if (callResult.error) {
                            return {
                                ...callResult,
                                ID: null,
                                TranID: null,
                                errors: [ callResult.error.message ],
                                exceptions: []
                            };
                        } else if (!callResult.result || !callResult.result.TranID) {
                            throw new Error("Result for TxnId is undefined, but no errors found");
                        } else {
                            sinkTransactionToZeeves(callResult.result.TranID);
                            return {
                                ...callResult,
                                ID: callResult.result.TranID,
                                TranID: callResult.result.TranID,
                                errors: [],
                                exceptions: []
                            };
                        }
                    } else {
                        return await originalZilliqa.wallet.provider.send(method, ...params);
                    }
                } catch (err) {
                    attemptsCount++;
                    if (attemptsCount > maxAttempts) {
                        throw err;
                    }
                    warn('Got error. Retry №' + attemptsCount + '...');
                }
            }
        } catch (err) {
            return {
                ID: null,
                TranID: null,
                errors: [],
                exceptions: [ err.stack ]
            }
        }
    };

    // Zeeves for now has only single testbot
    // which works also in mainnet
    localZeeves.wallet.net = 'mainnet';
    
    // Preventing TypeErrors
    localZeeves.wallet.defaultAccount = {
        base16: null,
        bech32: null,
        address: null,
    };

    // if user has already logged in the session (after last page reload)
    // this method will just return wallet data
    // otherwise, auth() method will be triggered (see below)
    localZeeves.getSession = async () => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                reject('Action timeout');
            }, CALL_TIMEOUT);
            try {
                if (getWalletInfo()) {
                    resolve(getWalletInfo());
                } else {
                    window.Zeeves.auth()
                        .then(result => {
                            resolve(result)
                        }).catch(err => {
                            reject(err && err.stack ? err.stack : JSON.stringify(err));
                        });
                }
            } catch(err) {
                reject(err  && err.stack? err.stack : JSON.stringify(err));
            }
        });
    };
    
    // main method to authorize user in the current session
    // opens iframe with Zeeves UI, which provides encrypted
    // keys to initialize account in current session
    localZeeves.auth = async () => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                reject('Action timeout');
            }, CALL_TIMEOUT);
            try {
                let messageReceived = false;

                const authWindow = openCenteredWindow(
                    baseUri + "/en/auth-sdk/"
                    + "?partnerUri=" + encodeURIComponent(location.host) 
                    + '&protocol=' + encodeURIComponent(location.protocol),
                    "authFrame", 
                    500, 500);

                if (!authWindow) {
                    localStorage.removeItem('zilswap:zeeves-connected'); //for zilswap
                    
                    //TODO: think about how to avoid popup blockings
                    //alert('Zeeves authentication popup was blocked. Enable popups in site settings.');
                    reject('Zeeves authentication popup was blocked. Enable popups in site settings.');
                }

                waitOpened()
                    .then(() => {
                        const pkPassword = randomUuid();
                        authWindow.postMessage('encryptionKey_' + pkPassword, baseUri);
        
                        const windowMonitor = setInterval(() => {
                            if (authWindow.closed && !messageReceived) {
                                clearInterval(windowMonitor);
                                reject("Error during Zeeves authentication");
                            }
                        }, 200);
        
                        listenMessages((e) => {
                            messageReceived = true;
                            if (e.origin === baseUri) {
                                const key = e.message ? "message" : "data";
                                const data = e[key];
                                if (typeof(data) === 'string') {
                                    const messagePayload = atob(data).split("_");
                                    if (messagePayload[0] === 'auth') {
                                        const encryptedPk = messagePayload[1];
                                        const pk = CryptoJS.AES.decrypt(encryptedPk, pkPassword).toString(CryptoJS.enc.Utf8);
                                        zilliqa.wallet.addByPrivateKey(pk);
                                        originalZilliqa.wallet.addByPrivateKey(pk);
                                        localZeeves.wallet.addByPrivateKey(pk);
                                        resolve(getWalletInfo());
                                    } else if (messagePayload[0] === 'error') {
                                        authWindow.close();
                                        reject(messagePayload[1]);
                                    }
                                }
                            } else {
                                window.console.error('Unknown origin of event - ' + e.origin);
                            }
                        });
                    })
                    .catch((err) => {
                        reject('Error while waiting for window to open - ' + err.stack);
                    })
            } catch (err) {
                reject(err ? err.stack : "Unknown error");
            }
        });
    };

    localZeeves.getMinimumGasPriceAndLimit = async(minGasLimit = 50) => {
        const gasLimit = Long.fromNumber(minGasLimit);
        const minGasPrice = await originalZilliqa.blockchain.getMinimumGasPrice();
        const gasPrice = new BN(parseInt(minGasPrice.result));

        return {
            gasPrice,
            gasLimit,
        };
    }

    localZeeves.sendTxn = async (toAddr, txnAmount) => {
        const walletInfo = await Zeeves.getSession();
        
        const gasPriceAndLimit = await localZeeves.getMinimumGasPriceAndLimit();

        const txnParams = {
            version: parseInt(bytes.pack(1, 1)),
            toAddr,
            amount: units.toQa(txnAmount, units.Units.Zil),
            ...gasPriceAndLimit
        };
        
        const payload = localZeeves.transactions.new(txnParams);
        const signedTxObj = await localZeeves.wallet.signWith(payload, walletInfo.byte20);
        const transaction = await localZeeves.blockchain.createTransactionWithoutConfirm(signedTxObj);

        return transaction;
    }

    localZeeves.wallet.sign = async (tx, offlineSign) => {
        if (typeof(tx) === 'string') {
            const userAccount = originalZilliqa.wallet.accounts[Object.keys(originalZilliqa.wallet.accounts)[0]];
            const message = tx;
            const digest = CryptoJS.SHA256(message).toString(CryptoJS.enc.Hex);
            const digestBytes  = Buffer.from(digest, "hex");
            const signature = userAccount.signTransaction(digestBytes);
            const publicKey = userAccount.publicKey;
            return { signature, publicKey };
        } else {
            return await originalZilliqa.wallet.sign(tx, offlineSign);
        }
    }

    localZeeves.mintNft = async (contractAddress, toAddress, itemUrl) => {
        const byteToAddress = fromBech32Address(toAddress);

        const args = [
            {
                vname: 'to',
                type: 'ByStr20',
                value: byteToAddress,
            },
            {
                vname: 'token_uri',
                type: 'String',
                value: itemUrl,
            }
        ];

        return await localZeeves.callContract(contractAddress, 'Mint', 0, args);
    }

    localZeeves.callContract = async (contractAddress, transition, txnAmount, args) => {
        const contract = localZeeves.contracts.at(contractAddress);

        const gasPriceAndLimit = await localZeeves.getMinimumGasPriceAndLimit(20000);

        const txnParams = {
            version: parseInt(bytes.pack(1, 1)),
            toAddr,
            amount: units.toQa(txnAmount, units.Units.Zil),
            ...gasPriceAndLimit
        };

        const txn = await contract.callWithoutConfirm(transition, args, txnParams);
        return txn;
    }

    localZeeves.setBaseUri = (newBaseUri) => {
        if (!newBaseUri) {
            return;
        }
        baseUri = newBaseUri;
    }

    localZeeves.setBackendUri = (newBackendUri) => {
        if (!newBackendUri) {
            return;
        }
        zeevesSinkUrl = newBackendUri;
    }

    Object.freeze(localZeeves);

    window.Zeeves = localZeeves;
})();
